import { useCallback, useEffect, useState } from 'react';
import {
  TUpdateCardHolderProps,
  useUpdateCardHolder,
} from '@xhype/card-app-sdk';
import { useGetUserData } from '@package/smart-wallet';
import * as Sentry from '@sentry/react';

/* Types */
export type TCardData = {
  cardTypeId: string;
  areaCode: string;
  mobile: string;
  firstName: string;
  lastName: string;
  cardHolderModel?: 'B2B' | 'B2C';
  birthday?: string;
  country?: string;
  town?: string;
  address?: string;
  postCode?: string;
  nationality?: string;
  gender?: 'M' | 'F';
  idType?: 'PASSPORT' | 'HK_HKID' | 'DLN' | 'GOVERNMENT_ISSUED_ID_CARD';
  idNumber?: string;
  issueDate?: string;
  idNoExpiryDate?: string;
  idFrontId?: string;
  idBackId?: string;
  idHoldId?: string;
  holderId?: number;
};

export type TUpdateCardHolderHookProps = {
  onError?: (error: any) => void;
  onSuccess?: () => void;
};

/* Hook */
export const useUpdateUserCardHolder = ({
  onError,
  onSuccess,
}: TUpdateCardHolderHookProps) => {
  const [userData, setUserData] = useState<undefined | TCardData>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo, refetch } = useGetUserData();

  /* Mutations */
  const updateCardHolder = useUpdateCardHolder({
    onError: (error: any) => {
      console.log(`Update Card Holder: Unexpected error - ${error.message}`);
      setError(error?.response?.data?.msg);
      onError?.(error);
    },
    onSuccess: () => {
      console.log('Update Card Holder: Successfully updated');
      refetch();
      onSuccess?.();
    },
  });

  /* Callbacks */
  const updateCardHolderData = useCallback(async (props: TCardData) => {
    setError(undefined);
    setUserData(props);
  }, []);

  const updateUserCardHolder = useCallback(async () => {
    if (!userData || !userInfo?.id) return;
    setIsLoading(true);

    try {
      const cardHolderData = {
        userId: userInfo.id,
        holderId: userData.holderId,
        cardTypeId: Number(userData.cardTypeId),
        cardHolderModel: userData.cardHolderModel,
        address: userData.address,
        areaCode: userData.areaCode,
        mobile: userData.mobile,
        firstName: userData.firstName,
        lastName: userData.lastName,
        birthday: userData.birthday,
        country: userData.country,
        town: userData.town,
        postCode: userData.postCode,
        nationality: userData.nationality,
        gender: userData.gender,
        idType: userData.idType,
        idNumber: userData.idNumber,
        issueDate: userData.issueDate,
        idNoExpiryDate: userData.idNoExpiryDate,
        idFrontId: userData.idFrontId,
        idBackId: userData.idBackId,
        idHoldId: userData.idHoldId,
      };

      const filteredCardHolderData = Object.fromEntries(
        Object.entries(cardHolderData).filter(
          ([, value]) => value !== undefined,
        ),
      );

      await updateCardHolder.mutateAsync(
        filteredCardHolderData as TUpdateCardHolderProps,
      );
    } catch (error) {
      console.error('Failed to update card holder:', error);
      Sentry.captureException({
        message: 'Failed to update card holder',
        extra: {
          error,
        },
      });
    } finally {
      setIsLoading(false);
      setUserData(undefined);
    }
  }, [userInfo, userData, updateCardHolder]);

  /* Effects */
  useEffect(() => {
    updateUserCardHolder();
  }, [userInfo, userData, updateUserCardHolder]);

  /* Return */
  return {
    updateCardHolderData,
    error,
    isLoading,
  };
};
